import React, { ReactElement, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { useNavigate } from 'react-router-dom';
import { TrainingPorgramsService } from 'services/TrainingProgramService';

interface ITrainingSummary {
  totalPrograms: number;
  activeCount: number;
  totalAssignments: number;
  totalCompleted: number;
  totalOverdue: number;
  completionRatio: number;
  count?: number;
}

interface InventoryRowProps {
  label: string;
  value: string | number;
  subText?: string;
}

const InventoryRow = ({
  label,
  value,
  subText,
}: InventoryRowProps): React.ReactElement => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    className="inventory-row"
    sx={{ py: 1 }}
  >
    <Stack
      direction="row"
      alignItems="center"
      gap={0.75}
      className="inventory-row-label-group"
    >
      <Typography
        className="inventory-row-label"
        sx={{ color: '#6b7280', fontSize: '14px' }}
      >
        {label}
      </Typography>
    </Stack>
    <Stack direction="column" alignItems="flex-end">
      <Typography
        className="inventory-row-value"
        sx={{ fontWeight: 600, color: '#052921', fontSize: '16px' }}
      >
        {value}
      </Typography>
      {subText && (
        <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
          {subText}
        </Typography>
      )}
    </Stack>
  </Stack>
);

const TrainingManagement = (): ReactElement => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [summary, setSummary] = useState<ITrainingSummary>({
    totalPrograms: 0,
    activeCount: 0,
    totalAssignments: 0,
    totalCompleted: 0,
    totalOverdue: 0,
    completionRatio: 0,
  });

  const loadTrainingData = (): void => {
    setIsLoading(true);
    TrainingPorgramsService.getTrainingPrograms('')
      .then((response) => {
        const { data } = response.data;

        const programs = data?.programs || [];
        const activeCount = programs.filter(
          (item: { isActive: boolean }) => item.isActive
        ).length;

        setSummary({
          totalPrograms: data?.totalPrograms || 0,
          activeCount,
          totalAssignments: data?.totalAssignments || 0,
          totalCompleted: data?.totalCompleted || 0,
          totalOverdue: data?.totalOverdue || 0,
          completionRatio: data?.completionRatio || 0,
        });
      })
      .catch((error) => {
        console.error('Error loading training data:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    loadTrainingData();
  }, []);

  const { totalPrograms, totalAssignments, totalOverdue, completionRatio } =
    summary;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardHeader
        title="Training Management"
        subheader="Overview of training programs and assignments"
        sx={{
          '& .MuiCardHeader-title': {
            fontSize: '18px',
            fontWeight: 600,
            color: '#052921',
          },
          '& .MuiCardHeader-subheader': {
            fontSize: '14px',
            color: '#6b7280',
          },
        }}
        action={
          <IconButton onClick={() => navigate('/training-programs')}>
            <RemoveRedEyeIcon />
          </IconButton>
        }
      />
      <CardContent>
        <Box sx={{ mt: 3 }}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Stack spacing={1}>
                <InventoryRow label="Total Programs" value={totalPrograms} />
                <InventoryRow
                  label="Total Assignments"
                  value={totalAssignments}
                />
                <InventoryRow
                  label="Completion Rate"
                  value={`${Math.ceil(completionRatio)}%`}
                />
                <InventoryRow
                  label="Overdue Assignments"
                  value={totalOverdue}
                />
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </CardContent>
    </Card>
  );
};

export default TrainingManagement;
